import { AccountBase, AccountBaseOptions } from './account-base';
import { Component } from '@opten/compiler/metadata/component';
import { memberStorage, profileService, accountService } from '../shop/shop-context';
import { UserSave } from '../models/profile';
import { HttpStatusCodes } from '../httpclient/status-codes';
import { RegExPatterns } from '../core/regexp';
import { Renderer } from '@opten/renderer';
import { IfConverter } from '@opten/renderer/converters/if.converter';
import { DefaultConverter } from '@opten/renderer/converters/default.converter';
import { InputConverter } from '@opten/renderer/converters/input.converter';
import { CustomInputConverter } from '../../utilities/custom-input.converter';
import { User } from '../models/customer';
import { take, filter, catchError } from 'rxjs/operators';
import { empty, of } from 'rxjs';

@Component({ className: 'js-shop-profile' })
export class ProfileComponent extends AccountBase {
	options: AccountBaseOptions = {
		selectors: {
			submitButton: 'js-shop-profile__submit'
		},
		disabled: '--disabled'
	};

	protected renderModel: {
		isGuest: boolean;
		submitSuccess?: boolean;
		submitError?: boolean;
		errorStatus?: string;
		passwordError?: boolean;
		passwordErrorStatus?: string;
		user: UserSave;
	};

	private _salutationId: HTMLInputElement;
	private _firstName: HTMLInputElement;
	private _lastName: HTMLInputElement;
	private _genderId: HTMLInputElement;
	private _dateOfBirth: HTMLInputElement;
	private _email: HTMLInputElement;
	private _telephone: HTMLInputElement;
	private _oldPassword: HTMLInputElement;
	private _newPassword: HTMLInputElement;
	private _repeatPassword: HTMLInputElement;

	onInit(): void {
		this.renderer = new Renderer([DefaultConverter, IfConverter, CustomInputConverter]);
		super.onInit();
		this._salutationId = this.formElement.elements.namedItem('salutationId') as HTMLInputElement;
		this._firstName = this.formElement.elements.namedItem('firstName') as HTMLInputElement;
		this._lastName = this.formElement.elements.namedItem('lastName') as HTMLInputElement;
		this._genderId = this.formElement.elements.namedItem('genderId') as HTMLInputElement;
		this._dateOfBirth = this.formElement.elements.namedItem('dateOfBirth') as HTMLInputElement;
		this._email = this.formElement.elements.namedItem('email') as HTMLInputElement;
		this._telephone = this.formElement.elements.namedItem('telephone') as HTMLInputElement;
		this._oldPassword = this.formElement.elements.namedItem('oldPassword') as HTMLInputElement;
		this._newPassword = this.formElement.elements.namedItem('newPassword') as HTMLInputElement;
		this._repeatPassword = this.formElement.elements.namedItem('repeatPassword') as HTMLInputElement;

		this.fieldsToValidate.push(
			this._salutationId,
			this._firstName,
			this._lastName,
			this._genderId,
			this._dateOfBirth,
			this._email,
			this._telephone
		);
	}

	protected reinitRendering(user: User) {
		this.renderModel.isGuest = true;
		if (user) {
			this.shopContext.onIsGuest.pipe(filter(isGuest => isGuest !== null)).subscribe(isGuest => {
				this.renderModel.isGuest = isGuest;
				if (this.renderModel.isGuest === false) {
					this.renderer.render(this.formElement, this.renderModel, this);
					profileService
						.getById(user.contractualPartner.id)
						.pipe(
							catchError(() => {
								return of(null);
							})
						)
						.subscribe(profile => {
							if (profile) {
								this.renderModel.user = profile.user;
								this.renderer.render(this.formElement, this.renderModel, this);
							}
						});
				} else {
					this.renderGuestProfile();
				}
			});
		} else {
			this.renderGuestProfile();
		}
	}

	private renderGuestProfile() {
		this.renderModel.user = {
			salutationId: null,
			firstName: '',
			lastName: '',
			genderId: null,
			dateOfBirth: null,
			mailAddress: '',
			telephone: ''
		};
		this.renderer.render(this.formElement, this.renderModel, this);
	}

	protected handleSubmit(): void {
		let hasNewPassword = false;
		let isPasswordValid = false;
		this.renderModel.user.salutationId = Number(this._salutationId.value);
		this.renderModel.user.firstName = this._firstName.value;
		this.renderModel.user.lastName = this._lastName.value;
		this.renderModel.user.genderId = Number(this._genderId.value);
		this.renderModel.user.dateOfBirth = new Date(this._dateOfBirth.value);
		this.renderModel.user.telephone = this._telephone.value;
		this.renderModel.user.mailAddress = this._email.value;

		if (this._newPassword.value) {
			const newpassword = this._newPassword.value;
			const repeatedPassword = this._repeatPassword.value;

			hasNewPassword = true;
			if (hasNewPassword) {
				this.renderModel.user.newPassword = this._newPassword.value;
				this.renderModel.user.currentPassword = this._oldPassword.value;
			}
			if (newpassword !== repeatedPassword) {
				this.renderModel.passwordError = true;
				this.renderModel.passwordErrorStatus = 'notSame';
				this.renderer.render(this.formElement, this.renderModel, this);
				isPasswordValid = false;
			} else if (!newpassword.match(RegExPatterns.passwordPattern) || !repeatedPassword.match(RegExPatterns.passwordPattern)) {
				this.renderModel.passwordError = true;
				this.renderModel.passwordErrorStatus = 'policy';
				this.renderer.render(this.formElement, this.renderModel, this);
				isPasswordValid = false;
			} else {
				isPasswordValid = true;
			}
		}

		if ((hasNewPassword === false || (hasNewPassword && isPasswordValid)) && this.validForm()) {
			accountService.updateAccount(this.renderModel.user).subscribe(
				user => {
					this.renderModel.passwordError = false;

					if (user) {
						memberStorage.set(user); //todo test if contractualpartners aren't deleted
						this.renderModel.submitError = false;
						this.renderModel.submitSuccess = true;
					} else {
						this.renderModel.submitError = true;
						this.renderModel.errorStatus = 'default';
					}
					this.renderer.render(this.formElement, this.renderModel, this);
				},
				error => {
					this.renderModel.submitError = true;
					this.renderModel.passwordError = false;
					switch (error.status) {
						case HttpStatusCodes.conflict:
							this.renderModel.errorStatus = 'conflict';
							break;
						case HttpStatusCodes.unauthorized:
							this.renderModel.errorStatus = 'unauthorized';
							break;
						default:
							this.renderModel.errorStatus = 'default';
							break;
					}
					this.renderer.render(this.formElement, this.renderModel, this);
				}
			);
		} else if (hasNewPassword === false) {
			this.renderModel.submitError = true;
			this.renderModel.errorStatus = 'default';
			this.renderer.render(this.formElement, this.renderModel, this);
		}
	}
}
